import React, { useState, useEffect, useCallback } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { loadOrCreateIdentity } from '../lib/identity';
import { getConversations, addMessageListener } from '../lib/messages';
import { getUserCommunities } from '../lib/communities';

type Row =
  | { kind: 'dm'; id: string; title: string; lastAt: number; unread: number }
  | { kind: 'community'; id: string; title: string; lastAt: number; memberCount: number };

const CHANNEL_MSGS_PREFIX = 'authenticulture_channel_messages';
const COMMUNITY_POSTS_PREFIX = 'authenticulture_community_posts';

// Latest activity timestamp across a community's channel messages + posts.
function communityLastActivity(communityId: string, fallback: number): number {
  let latest = fallback;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    const isChannel = key.startsWith(`${CHANNEL_MSGS_PREFIX}_${communityId}_`);
    const isPosts = key === `${COMMUNITY_POSTS_PREFIX}_${communityId}`;
    if (!isChannel && !isPosts) continue;
    try {
      const arr = JSON.parse(localStorage.getItem(key) || '[]') as Array<{ timestamp?: number }>;
      for (const m of arr) {
        if (typeof m.timestamp === 'number' && m.timestamp > latest) latest = m.timestamp;
      }
    } catch { /* ignore malformed */ }
  }
  return latest;
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

export function Inbox({ onOpenDM, onOpenCommunity, onBrowseCommunities }: {
  onOpenDM: (userId: string, username: string) => void;
  onOpenCommunity: (communityId: string) => void;
  onBrowseCommunities: () => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);

  const load = useCallback(() => {
    const identity = loadOrCreateIdentity();
    const dms: Row[] = getConversations(identity.userId).map(c => ({
      kind: 'dm',
      id: c.otherId,
      title: c.otherUsername,
      lastAt: c.lastAt,
      unread: c.unread,
    }));
    const communities: Row[] = getUserCommunities(identity.userId).map(c => ({
      kind: 'community',
      id: c.id,
      title: c.name,
      lastAt: communityLastActivity(c.id, c.createdAt),
      memberCount: c.memberCount,
    }));
    setRows([...dms, ...communities].sort((a, b) => b.lastAt - a.lastAt));
  }, []);

  useEffect(() => {
    load();
    return addMessageListener(load);
  }, [load]);

  return (
    <div style={{ flex: 1, overflowY: 'auto', backgroundColor: COLORS.bg, fontFamily: '"IBM Plex Mono", monospace' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        backgroundColor: COLORS.accent, color: '#fff',
        padding: `${SPACING.sm} ${SPACING.md}`,
        fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em',
        borderBottom: `2px solid ${COLORS.highlight}`, userSelect: 'none',
      }}>
        <span>INBOX</span>
        <button
          onClick={onBrowseCommunities}
          style={{
            background: COLORS.panel, color: COLORS.accent,
            border: `2px outset ${COLORS.highlight}`,
            padding: `2px ${SPACING.sm}`, cursor: 'pointer',
            fontFamily: '"IBM Plex Mono", monospace', fontSize: '9px', fontWeight: 700,
          }}
        >
          browse communities →
        </button>
      </div>

      {rows.length === 0 ? (
        <p style={{ padding: SPACING.xl, margin: 0, color: COLORS.textMuted, fontSize: '11px', lineHeight: 1.6, textAlign: 'center' }}>
          no messages or communities yet.<br />
          join a community or message someone to get started.
        </p>
      ) : (
        rows.map(row => (
          <button
            key={`${row.kind}-${row.id}`}
            onClick={() => row.kind === 'dm' ? onOpenDM(row.id, row.title) : onOpenCommunity(row.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: SPACING.md, width: '100%',
              padding: SPACING.md, background: 'none', cursor: 'pointer', textAlign: 'left',
              border: 'none', borderBottom: `1px solid ${COLORS.border}`,
              color: COLORS.text, fontFamily: '"IBM Plex Mono", monospace',
            }}
          >
            <div style={{
              width: 36, height: 36, flexShrink: 0,
              borderRadius: row.kind === 'dm' ? '50%' : '6px',
              background: row.kind === 'dm' ? COLORS.accent : COLORS.panel,
              border: row.kind === 'community' ? `2px outset ${COLORS.highlight}` : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: row.kind === 'dm' ? '11px' : '16px', fontWeight: 700,
            }}>
              {row.kind === 'dm' ? row.title.slice(0, 2).toUpperCase() : '⬡'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.kind === 'dm' ? `@${row.title}` : `# ${row.title}`}
              </div>
              <div style={{ fontSize: '9px', color: COLORS.textMuted, marginTop: 2 }}>
                {row.kind === 'dm' ? 'direct message' : `${row.memberCount} member${row.memberCount === 1 ? '' : 's'}`}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
              <span style={{ fontSize: '9px', color: COLORS.textMuted }}>{timeAgo(row.lastAt)}</span>
              {row.kind === 'dm' && row.unread > 0 && (
                <span style={{
                  backgroundColor: COLORS.accent, color: '#fff',
                  borderRadius: '50%', minWidth: 16, height: 16, padding: '0 4px',
                  fontSize: '9px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {row.unread > 9 ? '9+' : row.unread}
                </span>
              )}
            </div>
          </button>
        ))
      )}
    </div>
  );
}
